import { Alert, Typography } from "@mui/material";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCRUD } from "../../../api/crud";
import { useBreadcrumbs } from "../../../components/ui/breadcrumps/breadcrumbsContext";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../components/ui/detailView";
import { StatusChip } from "../../../components/ui/statusChip/statusChip.component";
import {
  KSEF_INVOICE_TYPE_LABEL_KEYS,
  PURCHASE_INVOICES_ENDPOINT,
} from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { PurchaseInvoice } from "../../../types/invoices";
import { formatDateTimeInline, formatNumber } from "../../../utils";
import { InvoiceParty } from "../invoiceParty/invoiceParty.component";

// Identifier types KSeF reports for the buyer; the rest carry no prefix.
const BUYER_IDENTIFIER_PREFIXES: Record<string, string> = {
  Nip: "NIP",
  VatUe: "VAT UE",
};

export default function ViewPurchaseInvoice() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get } = useCRUD(PURCHASE_INVOICES_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [invoice, setInvoice] = useState<PurchaseInvoice | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const canViewPurchaseInvoices = useHasPermission(
    "invoice.view_purchaseinvoice"
  );

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    const response = await get(id);
    const fetched = response?.data ?? null;
    setInvoice(fetched);
    setLoading(false);
    setOverride(`/invoices/purchase/${id}`, fetched ? fetched.invoice_number : "");
  }, [id, get, setOverride]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  if (!canViewPurchaseInvoices) {
    return null;
  }

  if (loading) {
    return <DetailPage loading />;
  }

  if (!invoice) {
    return <Typography>{t("Purchase invoice not found")}</Typography>;
  }

  const buyerIdentifierPrefix =
    BUYER_IDENTIFIER_PREFIXES[invoice.buyer_identifier_type];

  return (
    <DetailPage>
      <DetailHeader
        icon={<RequestQuoteOutlinedIcon />}
        title={`${t("Purchase invoice")} - ${invoice.invoice_number}`}
        subtitle={invoice.seller_name}
        chips={
          <>
            <StatusChip
              variant="outlined"
              label={t(KSEF_INVOICE_TYPE_LABEL_KEYS[invoice.invoice_type] ?? invoice.invoice_type)}
            />
            {invoice.has_attachment && (
              <StatusChip color="info" icon={<AttachFileIcon />} label={t("Has attachment")} />
            )}
            {invoice.is_self_invoicing && <StatusChip label={t("Self-invoicing")} />}
          </>
        }
      >
        <Alert severity="success">
          {t("KSeF number")}: {invoice.ksef_number}
        </Alert>
      </DetailHeader>

      <DetailGrid>
        <InvoiceParty title={t("Seller")} name={invoice.seller_name} identifier={invoice.seller_nip} />
        <InvoiceParty
          title={t("Buyer")}
          name={invoice.buyer_name}
          identifierLabel={buyerIdentifierPrefix ?? t("Identifier")}
          identifier={invoice.buyer_identifier}
        />

        <DetailSection title={t("Dates and payment")}>
          <DetailRow label={t("Date of issue")} value={invoice.issued_at} />
          <DetailRow label={t("Received by KSeF at")} value={formatDateTimeInline(invoice.invoicing_date)} />
          <DetailRow label={t("Stored in KSeF at")} value={formatDateTimeInline(invoice.permanent_storage_date)} />
          <DetailRow label={t("Invoicing mode")} value={invoice.invoicing_mode} />
        </DetailSection>

        <DetailSection title={t("Summary")}>
          <DetailRow label={t("Net total")} value={`${formatNumber(invoice.net_amount)} ${invoice.currency}`} />
          {/* KSeF reports the VAT total in PLN whatever the invoice currency. */}
          <DetailRow label={t("VAT total")} value={`${formatNumber(invoice.vat_amount)} PLN`} />
          <DetailRow label={t("Gross value")}>
            <DetailValue>
              <strong>{`${formatNumber(invoice.gross_amount)} ${invoice.currency}`}</strong>
            </DetailValue>
          </DetailRow>
        </DetailSection>
      </DetailGrid>

      <DetailMetadata createdAt={invoice.created_at} />
    </DetailPage>
  );
}
